const PRODUCTION_GA_MEASUREMENT_ID = "G-Y8CSJ58ZSD";
const DISABLED_VALUES = new Set(["", "0", "false", "off", "disabled"]);
const DEFAULT_PAGE_TITLE = "Curtis Mullins";
const MAX_TITLE_LENGTH = 120;
const MAX_EVENT_VALUE_LENGTH = 100;

const ALLOWED_HASHES = new Set([
  "#home",
  "#about",
  "#work-history",
  "#replikit",
  "#projects",
  "#education",
  "#volunteering",
  "#skills",
  "#contact",
]);

let analyticsInitialized = false;

const canUseBrowser = () =>
  typeof window !== "undefined" && typeof document !== "undefined";

const stripControlCharacters = (value) =>
  value
    .split("")
    .map((character) => {
      const codePoint = character.charCodeAt(0);
      return codePoint > 31 && codePoint !== 127 ? character : " ";
    })
    .join("");

export const getAnalyticsMeasurementId = () => {
  const configuredId = process.env.REACT_APP_GA_MEASUREMENT_ID;

  if (typeof configuredId === "string") {
    const normalizedId = configuredId.trim();
    return DISABLED_VALUES.has(normalizedId.toLowerCase()) ? "" : normalizedId;
  }

  return process.env.NODE_ENV === "production" ? PRODUCTION_GA_MEASUREMENT_ID : "";
};

const sanitizeTitle = (title) => {
  const normalizedTitle = stripControlCharacters(String(title || DEFAULT_PAGE_TITLE))
    .replace(/\s+/g, " ")
    .trim();

  return (normalizedTitle || DEFAULT_PAGE_TITLE).slice(0, MAX_TITLE_LENGTH);
};

const sanitizePathname = (pathname) => {
  const normalizedPath = String(pathname || "/").trim();
  const withLeadingSlash = normalizedPath.startsWith("/")
    ? normalizedPath
    : `/${normalizedPath}`;

  return withLeadingSlash.replace(/\/{2,}/g, "/");
};

const sanitizeHash = (hash) => {
  const normalizedHash = String(hash || "").trim().toLowerCase();
  return ALLOWED_HASHES.has(normalizedHash) ? normalizedHash : "";
};

export const getSanitizedPageView = ({
  pageLocation = canUseBrowser() ? window.location : undefined,
  pageTitle = canUseBrowser() ? document.title : DEFAULT_PAGE_TITLE,
} = {}) => {
  const fallbackOrigin = "https://curtismullins.com";
  const sourceUrl =
    typeof pageLocation === "string"
      ? pageLocation
      : pageLocation?.href || fallbackOrigin;

  let parsedUrl;

  try {
    parsedUrl = new URL(sourceUrl, pageLocation?.origin || fallbackOrigin);
  } catch {
    parsedUrl = new URL(fallbackOrigin);
  }

  const pathname = sanitizePathname(parsedUrl.pathname);
  const hash = sanitizeHash(parsedUrl.hash);
  const pagePath = `${pathname}${hash}`;

  return {
    page_location: `${parsedUrl.origin}${pagePath}`,
    page_path: pagePath,
    page_title: sanitizeTitle(pageTitle),
  };
};

const ensureGtag = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(Array.from(arguments));
    };
};

const loadGtagScript = (measurementId) => {
  const existingScript = document.querySelector("script[data-analytics='ga4']");
  if (existingScript) {
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.dataset.analytics = "ga4";
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    measurementId,
  )}`;

  document.head.appendChild(script);
};

export const initAnalytics = (measurementId = getAnalyticsMeasurementId()) => {
  if (!canUseBrowser() || !measurementId || analyticsInitialized) {
    return false;
  }

  ensureGtag();
  loadGtagScript(measurementId);

  window.gtag("js", new Date());
  window.gtag("config", measurementId, {
    send_page_view: false,
  });

  analyticsInitialized = true;
  return true;
};

export const sendPageView = ({
  measurementId = getAnalyticsMeasurementId(),
  pageLocation,
  pageTitle,
} = {}) => {
  if (!canUseBrowser() || !measurementId) {
    return false;
  }

  initAnalytics(measurementId);

  const pageView = getSanitizedPageView({
    pageLocation,
    pageTitle,
  });

  window.gtag("event", "page_view", pageView);
  return pageView;
};

const sanitizeEventName = (eventName) => {
  const normalizedName = String(eventName || "").trim();
  return /^[A-Za-z][A-Za-z0-9_]{0,39}$/.test(normalizedName)
    ? normalizedName
    : "";
};

const sanitizeEventParams = (params) =>
  Object.entries(params || {}).reduce((safeParams, [key, value]) => {
    if (!/^[A-Za-z][A-Za-z0-9_]{0,39}$/.test(key)) {
      return safeParams;
    }

    if (typeof value === "number" || typeof value === "boolean") {
      safeParams[key] = value;
      return safeParams;
    }

    if (typeof value === "string") {
      safeParams[key] = stripControlCharacters(value)
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, MAX_EVENT_VALUE_LENGTH);
    }

    return safeParams;
  }, {});

export const trackAnalyticsEvent = (
  eventName,
  params = {},
  { measurementId = getAnalyticsMeasurementId(), pageLocation, pageTitle } = {},
) => {
  const safeEventName = sanitizeEventName(eventName);

  if (!canUseBrowser() || !measurementId || !safeEventName) {
    return false;
  }

  initAnalytics(measurementId);

  const pageView = getSanitizedPageView({
    pageLocation,
    pageTitle,
  });

  window.gtag("event", safeEventName, {
    ...sanitizeEventParams(params),
    ...pageView,
  });

  return true;
};

export const startAnalytics = () => {
  if (!canUseBrowser() || !getAnalyticsMeasurementId()) {
    return () => {};
  }

  sendPageView();

  const sendCurrentPageView = () => {
    sendPageView();
  };

  window.addEventListener("hashchange", sendCurrentPageView);
  window.addEventListener("popstate", sendCurrentPageView);

  return () => {
    window.removeEventListener("hashchange", sendCurrentPageView);
    window.removeEventListener("popstate", sendCurrentPageView);
  };
};
